<template>
  <div class="controls">
    <div class="controls__start">
      <HelpTip>
        Select a tab below to see various training session details. To validate this model, click "VALIDATE".
        Click "COMPARE" to compare validation results.
        Note, that a training session may have multiple validation sessions with different parameters.
      </HelpTip>
    </div>
    <div class="controls__end">
      <div class="controls__actions">
        <button v-tooltip="'Retrieve info, logs, charts for training session.'" class="button-light" @click="handleRefreshPage">
          <SVGIcon
            :iconName="'refresh'"
            :width="'18px'"
            :height="'18px'"
          />Refresh
        </button>
        <button
          v-if="permissions && permissions.trainer_write"
          v-tooltip="'Start validate session.'"
          class="button-light"
          @click="handleOpenStartValidatorModal"
        >
          <SVGIcon
            :iconName="'compare'"
            :width="'18px'"
            :height="'18px'"
          />Validate
        </button>
        <button v-tooltip="'Compare multiple validation results.'" class="button-light" @click="() => compareValidateSessionsModal.showModal()">
          <SVGIcon
            :iconName="'chart_multiple'"
            :width="'18px'"
            :height="'18px'"
          />Compare
        </button>
        <button
          v-if="permissions && permissions.trainer_write"
          v-tooltip="`Terminate training but keep all logs and charts.`"
          class="button-light"
          @click="openConfirmStopTrainingSessionModal"
        >
          <SVGIcon
            :iconName="'stop_circle'"
            :width="'18px'"
            :height="'18px'"
          />
          Stop
        </button>
        <button
          v-if="permissions && permissions.trainer_write"
          v-tooltip="`Move training session to recycle bin. This will also move training artifacts and validate sessions to recycle bin.`"
          class="button-light delete"
          @click="openConfirmDeleteSessionModal"
        >
          <SVGIcon
            :iconName="'trash'"
            :width="'18px'"
            :height="'18px'"
          />
          Recycle Bin
        </button>
      </div>
    </div>
  </div>
  <div class="training-session scrollbar">
    <div class="session-data">
      <div class="tab-selector mb-1">
        <input
          id="option-info"
          v-model="tab"
          type="radio"
          name="tab-selector"
          :value="'info'"
        >
        <label for="option-info">Session</label>
        <!-- <input
                id="option-metrics"
                v-model="tab"
                type="radio"
                name="tab-selector"
                :value="'metrics'"
              >
              <label for="option-metrics">Metrics</label> -->
        <input
          v-if="!isPublicRoute"
          id="option-logs"
          v-model="tab"
          type="radio"
          name="tab-selector"
          :value="'logs'"
        >
        <label v-if="!isPublicRoute" for="option-logs">Logs</label>

        <input
          id="option-charts"
          v-model="tab"
          type="radio"
          name="tab-selector"
          :value="'charts'"
        >
        <label for="option-charts">Charts</label>

        <input
          id="option-models"
          v-model="tab"
          type="radio"
          name="tab-selector"
          :value="'models'"
        >
        <label for="option-models">Artifacts</label>

        <input
          v-if="!isPublicRoute"
          id="option-runs"
          v-model="tab"
          type="radio"
          name="tab-selector"
          :value="'runs'"
        >
        <label v-if="!isPublicRoute" for="option-runs">Runs</label>
      </div>
      <div v-if="tab==='info'" class="params session-info-layout">
        <SessionData
          :currentSession="currentSession"
          :user="user"
          :currentSessionStatus="currentSessionStatus"
          :validateSessions="validateSessions"
          :modelMap="modelMap"
          :loading="loading"
          :currentProject="currentProject"
          :currentExperiment="currentExperiment"
          @validate-session-click="handleValidateSessionClick"
          @to-gallery="handleToGallery"
        />
        <SessionParameters :modelParameters="model_parameters" />
        
        <!-- Trainer Processes -->
        <div class="trainer-processes-section">
          <h3 class="section-heading">Trainer Processes</h3>
          <TrainerProcesses
            v-if="currentSession"
            :status="currentSessionStatus"
            :processes="currentSession.processes || []"
            :task-u-i-d="encodeID(currentSession?.id, 'bt-')"
            :user-name="user?.username"
            :parent-u-i-d="encodeID(currentSession?.parent?.uid || currentSession?.parent?.id, currentSession?.type)"
            style="width: 100%"
          />
        </div>
      </div>

      <div v-else-if="tab==='metrics'" class="metrics">
        <div id="training-session-metrics" class="text-area-details-white scrollbar">
          <div
            v-for="(value, name, index) in metrics"
            :key="index"
            class="list-item"
            :title="value"
          >
            <label>{{ name }}</label>
            <span>{{ value }}</span>
          </div>
        </div>
      </div>
      <div v-else-if="tab==='logs' && !isPublicRoute" class="system-logs">
        <LogViewer
          v-if="currentSession && currentSession.docker_task"
          :taskId="currentSession.docker_task.id"
        />
      </div>
      <div v-else-if="tab==='charts'" class="params">
        <TrainingCharts
          ref="trainingCharts"
          :trainerSession="currentSession"
          :projectID="currentProject.id"
        />
      </div>
      <div v-else-if="tab==='models'" class="artifacts-tab">
        <div class="artifacts-tab__section">
          <ArtifactsList
            v-model="artifacts"
            title="Artifacts"
            :sessionId="currentSession?.id"
          />
        </div>
        <div class="artifacts-tab__section">
          <AppsMiniList
            title="Converters"
            type="converter"
            :defaultValues="{
              trainer: {
                session_id: currentSession?.id,
                session_name: currentSession?.name,
                experiment_id: currentExperiment?.id,
                project_id: currentProject?.id
              }
            }"
          />
        </div>
      </div>
      <div v-else-if="tab==='runs' && !isPublicRoute" class="runs-tab">
        <RunsList
          :train_session_id="currentSession?.id"
          :val_session_id="currentSession?.parent?.id"
        />
      </div>
    </div>
  </div>
  <ConfirmModal
    ref="confirmModal"
    :messageHeader="confirmMessageHeader"
    :message="confirmMessage"
    :buttonClass="'button-delete'"
    :buttonText="buttonText"
    @confirmed="confirmFunction"
  />
  <StartValidatorModal
    ref="startValidatorModal"
    :datasetId="modalDatasetId"
    :annotationSetId="modalAnnotationSetId"
    :trainSessionId="currentSession ? currentSession.id : null"
    @validate-session-started="handleValidateSessionCreated"
  />
  <CompareValidateSessionModal
    ref="compareValidateSessionsModal"
    :sessions="validateSessions"
    @open-compare="(sessionIds) => handleOpenCompareSessionsPage(sessionIds)"
  />
</template>

<script setup>
import {
  ref, computed, onBeforeUnmount, onMounted, watch, inject,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { encodeID } from '@/assets/js/utils.js';
import { goto_trainers } from '@/assets/js/links.js';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import * as YAML from 'yaml';
import { useTimeoutPoll } from '@vueuse/core';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import { RecycleScroller } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import { useStore } from 'vuex';
import TrainingCharts from '@/components/TrainerComponent/TrainerExperimentAndSessions/TrainingCharts.vue';
import ArtifactsList from '@/components/TrainerComponent/TrainerExperimentAndSessions/ArtifactsList.vue';
import SessionData from '@/components/TrainerComponent/TrainerExperimentAndSessions/SessionData.vue';
import SessionParameters from '@/components/TrainerComponent/TrainerExperimentAndSessions/SessionParameters.vue';
import TrainerProcesses from '@/components/TrainerProcesses/TrainerProcesses.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import StartValidatorModal from '@/components/ValidationPage/StartValidatorModal.vue';
import CompareValidateSessionModal from '@/components/TrainerComponent/TrainerExperimentAndSessions/CompareValidateSessionModal.vue';
import HelpTip from '../../HelpTip.vue';
import AppsMiniList from '@/components/Apps/AppsMiniList.vue';
import LogViewer from '@/components/Apps/LogViewer.vue';
import RunsList from '@/components/TrainerComponent/TrainerExperimentAndSessions/RunsList.vue';
// Refs
const isPublic = inject('isPublic', false);
const store = useStore();
const tab = ref('info');
const logs = ref(null);
const artifacts = ref([]);
const logLines = ref([]);
const $toast = useToast();
const route = useRoute();
const router = useRouter();
const confirmMessage = ref(null);
const confirmMessageHeader = ref(null);
const toDelete = ref(null);
const confirmFunction = ref(null);
const buttonText = ref('Delete');
const confirmModal = ref(null);
const validateSessions = ref(null);
const hideProgress = ref(false);
const loading = ref(false);
const startValidatorModal = ref(null);
const modalDatasetId = ref(0);
const modalAnnotationSetId = ref(0);
const compareValidateSessionsModal = ref(null);
const trainingCharts = ref(null);
const permissions = ref(null);

const show_start_options = ref(true);
const show_progress = ref(false);

let deployUpdateTime = null;

defineProps({
  projectId: {
    type: [String, Number],
    required: false,
  },
  sessionId: {
    type: [String, Number],
    required: false,
  },
})

const deploymentProgress = ref({
  "status": "Running",
  "docker_status": "",
  "message": "Initializing...",
  "error_messages": null,
  "date": "2025-03-26T03:49:08.767694Z",
  "end_date": "2025-03-26T04:29:29.305341Z",
  "data": {
    "stage_definitions": [
      {
        "convert": "Model Conversion",
      },
      {
        "package": "Packaging Model",
      },
    ],
    "stages": {
      "convert": {
        "label": "Caching group: train",
        "percentage": 0,
        "stage": "cache-train",
        "status": "Running",
      },
      "package": {
        "label": "Caching group: val",
        "percentage": 0,
        "stage": "cache-val",
        "status": "Running",
      },
    },
  },
  "count": 0,
  "total": 0,
});

const currentSession = ref(null);
const currentSessionStatus = computed(() => currentSession.value?.docker_task?.status);

const userList = computed(() => store.state.user.userList);
const user = computed(() => userList.value?.find((e) => e.username === currentSession.value?.docker_task.username));

const currentProject = computed(() => store.state.projects.currentProject);

const currentExperiment = computed(() => store.state.experiments.currentExperiment);

const modelMap = ref({
  "modelpack": "ModelPack",
  "fusion": "EdgeFirst Fusion",
});

const isPublicRoute = computed(() => route.path.includes('/public'));

const isTabAllowed = (tabName) => {
  if (!isPublicRoute.value) {
    return true;
  }
  return tabName !== 'logs' && tabName !== 'runs';
};

const metrics = computed(() => {
  const result = {};
  for (const [key, value] of Object.entries(currentSession.value?.metrics || {})) {
    if (key !== 'timings' && key !== 'model' && key !== 'dataset'
        && key !== 'class_histogram_data' && key !== 'confusion_matrix_data' && key !== 'precision_recall_data') {
      result[key] = value;
    }
  }
  return result;
});

const model_parameters = computed(() => YAML.stringify(currentSession.value?.params?.model_params || {}, { indent: 8 }));

// Lifecycle hooks

const getLogs = async () => {
  if (currentSession.value && currentSession.value.id) {
    const dataConnect = new DatastoreConnect();
    const resp = await dataConnect.getTrainingSessionLogs({
      session_id: currentSession.value.id,
    });

    let temp = "";
    if (!resp || resp.error || !resp.result) {
      temp += `No logs file to display`;
    } else {
      temp += resp.result;
    }

    logs.value = temp;
  } else {
    logs.value = null;
  }

  if (logs.value) {
    logLines.value = logs.value.split('\n');
  }
};

const { isActive: isGetLogsActive, pause: pauseGetLogs, resume: resumeGetLogs } = useTimeoutPoll(getLogs, 5000);

const handleModelDeploy = () => {
  show_progress.value = true;
  show_start_options.value = false;
  let modelConvertProgress = 0;
  let modelDeployProgress = 0;
  deployUpdateTime = setInterval(() => {
    modelConvertProgress++;
    if (modelConvertProgress > 100) modelConvertProgress = 100;
    deploymentProgress.value.data.stages['convert'].percentage = modelConvertProgress;

    if (modelConvertProgress === 100) {
      modelDeployProgress++;
      if (modelDeployProgress > 100) modelDeployProgress = 100;
      deploymentProgress.value.data.stages['package'].percentage = modelDeployProgress;
    }
  }, 300);
};
const handleCancelModelDeploy = () => {
  show_progress.value = false;
  show_start_options.value = true;
  clearInterval(deployUpdateTime);
  deployUpdateTime = null;
  deploymentProgress.value.data.stages['convert'].percentage = 1;
  deploymentProgress.value.data.stages['package'].percentage = 0;
};

const handleRefreshPage = () => {
  if (tab.value === "charts") {
    trainingCharts.value.getChartsData();
  } else if (tab.value === "logs") {
    getLogs();
  }
  GetSession();
};

const handleGetSession = async (session_id) => {
  if (!session_id || currentSession.value.id) return;
};

const GetSession = async (session_id) => {
  if (!session_id) return;
  
  validateSessions.value=[];

  const dataConnect = new DatastoreConnect();
  await dataConnect.getTrainingSession({ trainer_session_id: session_id })
    .then(async (data) => {
      if (data.result) {
        const date1 = new Date(data.result.deleted);
        const date2 = new Date("0001-01-01T00:00:00Z");
        if (date1 > date2) {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Session has been removed`,
            type: "error",
            position: "top",
          });

          return false;
          
        } else {
          currentSession.value = data.result;

          for (const process of currentSession.value.processes || []) {
           if (process.process_type === 'validation') {
              process.id=process.val_session_id;
              validateSessions.value.push(process);  
            }
          }

          return true;
        }
      } else if (data.error) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to retrieve training session: ${data.error.message}`,
          type: "error",
          position: "top",
        });
        return false;
      }
    })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to retrieve training session: ${error}`,
        type: "error",
        position: "top",
      });
      return false;
    });
    return false;
};

const { isActive: isGetSessionsActive, pause: pauseGetSession, resume: resumeGetSession } = useTimeoutPoll(GetSession, 30000);

const handleToGallery = () => {
  if (currentSession.value.params.dataset_params['dataset_id']) {
    router.push({ name: `${isPublic ? 'public.' : ''}datasets.gallery.new`, params: { datasetId: Number(currentSession.value.params.dataset_params['dataset_id']).toString(16) } });
  }
};

const openConfirmStopTrainingSessionModal = () => {
  if (currentSession.value) {
    confirmMessage.value = `Are you sure you want to stop training session "${currentSession.value.name}"?`;
    confirmMessageHeader.value = 'Stop Training Session';
    buttonText.value = 'Stop';
    confirmModal.value.showModal();
    confirmFunction.value = () => {
      handleStopTrainingSessions([currentSession.value.id]);
    };
  }
};
const openConfirmDeleteSessionModal = () => {
  if (currentSession.value) {
    confirmMessage.value = `Are you sure you want to move training session "${currentSession.value.name}" to recycle bin? This will also move training artifacts and validate sessions to recycle bin.`;
    confirmMessageHeader.value = 'Remove Training Session';
    buttonText.value = 'Remove';
    confirmModal.value.showModal();
    confirmFunction.value = () => {
      handleDeleteSession(currentSession.value.id);
    };
  }
};

const handleStopTrainingSessions = async (ids) => {
  const dataConnect = new DatastoreConnect();
  await dataConnect.terminateTrainingSessions({
    session_ids: ids,
  })
    .then((data) => {
      GetSession();
      if (data.error) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to stop training session: ${data.error.message}`,
          type: "error",
          position: "top",
        });
      }
    })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to stop training session: ${error}`,
        type: "error",
        position: "top",
      });
    });

  toDelete.value = null;
};
const handleDeleteSession = async (id) => {
  const dataConnect = new DatastoreConnect();
  await dataConnect.deleteTrainingSession({
    session_id: id,
  })
    .then(async (data) => {
      if (data.error) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to delete training session: ${data.error.message}`,
          type: "error",
          position: "top",
        });
      }
      else {
        navigateToTrainers();
      }
    })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to delete training session: ${error}`,
        type: "error",
        position: "top",
      });
    });

  toDelete.value = null;
};

// const getValidateSessionListByTrainingSession = async (id) => {
//   const dataConnect = new DatastoreConnect();
//   await dataConnect.getValidateSessionListByTrainingSession({
//     training_session_id: id,
//   })
//     .then(async (data) => {
//       if (data.error) {
//         $toast.open({
//           queue: true,
//           dismissible: true,
//           message: `Failed to retrieve validate sessions: ${data.error.message}`,
//           type: "error",
//           position: "top",
//         });
//       } else if (data.result) {
//         validateSessions.value = data.result;
//       }
//     })
//     .catch((error) => {
//       $toast.open({
//         queue: true,
//         dismissible: true,
//         message: `Failed to retrieve validate sessions: ${error}`,
//         type: "error",
//         position: "top",
//       });
//     });

//   toDelete.value = null;
// };

const navigateToTrainers = () => {
  let projID=currentProject.value.id;
  let expID=currentSession.value.trainer_id;
  goto_trainers(projID, expID);
};

async function getProjectAccessForSingleUser() {
  if (currentProject.value && store.state.user?.user?.username) {
    const dataConnect = new DatastoreConnect();
    await dataConnect.getProjectAccessForSingleUser({
      project_id: currentProject.value.id,
      username: store.state.user.user.username,
    })
      .then((data) => {
        if (data.result) {
          permissions.value = data.result;
        } else if (data.error) {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve project access: ${data.error.message}`,
            type: "error",
            position: "top",
          });
        }
      })
      .catch((e) => {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to retrieve project access: ${e}`,
          type: "error",
          position: "top",
        });
      });
  }
}

const handleValidateSessionClick = (validateSession) => {
  router.push({ name: `${isPublic ? 'public.' : ''}experiment.validation.details`, params: { sessionId: encodeID(validateSession.id, 'v') } });
};

const handleValidateSessionCreated = async () => {
  await getValidateSessionListByTrainingSession(currentSession.value.id);
};

const handleOpenStartValidatorModal = () => {
  if (currentSession.value && currentSession.value.params.dataset_params) {
    modalDatasetId.value = currentSession.value.params.dataset_params['dataset_id'];
    modalAnnotationSetId.value = currentSession.value.params.dataset_params['annotation_set_id'];
  }
  startValidatorModal.value.showModal();
};

const handleOpenCompareSessionsPage = (sessionIds) => {
  router.push({ name: `${isPublic ? 'public.' : ''}validation.compare`, params: { expId: encodeID(currentExperiment.value.id, 'exp') }, query: { session_ids: encodeURI(JSON.stringify(sessionIds)) } });
};

watch(tab, () => {
  if (!isTabAllowed(tab.value)) {
    tab.value = 'info';
    return;
  }

  // console.log('Tab changed to', tab.value, currentSession.value);

  if (!currentSession.value || !currentSession.value.id){
    console.warn('No current session available, cannot update route or fetch data.');
    return;
  } 

  router.replace({ name: route.name, params: { sessionId: encodeID(currentSession.value.id, 't') }, query: { mode: tab.value } });

  if (tab.value === 'logs') {
    resumeGetLogs();
  } else {
    pauseGetLogs();
  }

  if (tab.value === 'info') {
    resumeGetSession();
  } else {
    pauseGetSession();
  }
});

onBeforeUnmount(() => {
  pauseGetLogs();
  pauseGetSession();
});

onMounted(async () => {

  deploymentProgress.value.data.stages['convert'].percentage = 1;
  const mode = route.query.mode;
  if (mode && isTabAllowed(mode)) {
    tab.value = mode;
  } else if (mode) {
    tab.value = 'info';
  }

  // Handle both numeric ID and hex UID format (t-hexvalue)
  const sessionIdParam = route.params.sessionId;
  let session_id = null;
  
  if (sessionIdParam) {
    if (sessionIdParam.startsWith('t-')) {
      // Convert hex format (t-7bf) to decimal
      session_id = parseInt(sessionIdParam.substring(2), 16);
    } else {
      // Try to parse as numeric, otherwise use as-is
      const parsedId = parseInt(sessionIdParam);
      session_id = isNaN(parsedId) ? sessionIdParam : parsedId;
    }
  }


  loading.value = true;
  await GetSession(session_id);
  // If no session ID in route and no current session, redirect to training list
  if (!currentSession.value) {
      loading.value = false;
      return;
  }

  //No need as session are include in trainer get
  // await getValidateSessionListByTrainingSession(session_id);
  getProjectAccessForSingleUser();

  if (tab.value === 'info') {
    resumeGetSession();
  }

  loading.value = false;
});

</script>

<style lang="scss" scoped>
@import '@/assets/css/themes/auzone/buttons.scss';

.deploy-div {
  display: flex;
  flex-direction: column;
  min-width: 200px;
  max-width: 400px;
  margin: 10px;
}

.controls {
  display: flex;
  margin: 20px 20px 0 20px;
  align-items: flex-start;
  gap: 10px;

  &__count {
    font-weight: bold;
    font-size: 1rem;
  }

  &__start {
    display: flex;
    justify-content: flex-start;
    width: fit-content;
    align-items: flex-start;
    gap: 10px;
  }

  &__end {
    display: flex;
    justify-content: flex-end;
    flex: 1;
  }

  &__actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    height: 100%;
  }
}

@media (max-width: $breakpoint-sm) {
  .controls {
    flex-direction: column;
    &__start{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__middle{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__end{
      display: flex;
      align-items: flex-end;
      flex-direction: column;
      width: 100%;
    }
  }
}

.training-session{
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  overflow-x: auto;
}

.session-data {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  height: 100%;
  width: calc(100% - 400px);
  padding: 16px;

  ul {
    list-style-type: none;
  }

  h3 {
    margin: 0;
    margin-bottom: 10px;
    font-size: 1rem;
    text-align: left;
    @include themify() {
      color: themed('body-text-color-secondary');
    }
  }

  .text-area-box {
    display: flex;
    flex-direction: row;
    border-radius: 4px;
    background: #121212;
    border: 1px solid grey;
    height: 100%;
    // padding: 15px;
    overflow-y: auto;

    span {
      color: white;
      font-weight: 700;
      font-size: 0.9rem;

      pre {
        font-family: 'Courier New', monospace !important;
        text-align: left;
      }
    }
  }

  .text-area-details-white {
    display: grid;
    grid-template-columns: repeat(auto-fit, 450px);
    grid-auto-rows: 50px;
    grid-gap: 20px 5px;
    height: 100%;
    border-radius: 4px;
    background: var(--color-white-100);
    border: 1px solid var(--color-primary-100);
    padding: 15px;
    overflow-y: auto;

    .list-item {
      height: fit-content;
      display: flex;
      flex-direction: column;
      text-align: left;
      padding: 5px;

      label {
        @include themify() {
          color: tint-color(themed('body-text-color-secondary'), 20%);
        }
        font-weight: 700;
        font-size: 0.9rem;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      span {
        @include themify() {
          color: themed('body-text-color');
        }
        font-weight: 600;
        font-size: 0.9rem;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
  }

  .system-logs, .metrics, .params {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow-y: auto;
  }
}

.tab-selector {
  display: flex;
  flex-direction: row;
  border-bottom: 1px solid #ddd;
}

.tab-selector input[type="radio"] {
  display: none;
}

.tab-selector label {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  height: 32px;
  width: fit-content;
  font-size: 0.9rem;
  font-weight: bold;
  color: var(--body-text-color-heading);
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
}

.tab-selector input[type="radio"]:checked+label {
  @include themify() {
    color: themed('color-primary-500');
    border-bottom: 2px solid themed('color-primary-500');
  }
}

.session-info-layout {
  display: grid !important;
  grid-template-columns: repeat(2, 1fr);
  gap: 0;
  align-items: start;
  flex-direction: unset !important;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
}

.artifacts-tab {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  padding: 8px 0;

  &__section {
    flex: 1 1 300px;
    min-width: 0;

    > * {
      width: 100%;
      min-width: 0;
    }
  }
}

.artifacts {
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  min-height: 0;
  padding: 4px;
  max-width: 400px;
}

.download-model-card {
  cursor: pointer;
  &:hover {
    box-shadow: 0px 3px 4px rgba(0, 0, 0, 0.375), 0 0 2px 2px rgba(0, 0, 0, 0.125);
  }
}

.logs-scroller {
  height: 100%;
  width: 100%;
}

.log-line {
  text-align: left;
  white-space: pre;
  color: white;
}

:deep(.vue-recycle-scroller__item-wrapper) {
  overflow: visible !important;
}

:deep(.vue-recycle-scroller) {
  overflow: auto !important;
  padding: 15px;
}

.trainer-processes-section {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #ddd;
}

.section-heading {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 16px;
  @include themify() {
    color: themed('body-text-color');
  }
}

</style>
